import { storiesWithSlugs, type Story } from "@/lib/results-stories";

// Brand-suffixed titles run slightly past the ~60-char SERP snippet, but a
// single, identical brand token on every page is what search engines use to
// resolve the site to one entity, so the suffix is never dropped.
// 70 is the Twitter/X card title limit; titles double as twitter:title, so
// staying at or under it keeps the social preview identical to the <title>.
const MAX_TITLE = 70;
const BRAND = "Virginia Bariatric";

/** Truncate on a word boundary so titles never end mid-word. */
function clampWords(text: string, max: number) {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(" ");
  return (space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[-–—,;:\s]+$/, "");
}

/**
 * Preferred title forms for a story, longest (most branded) first.
 * Order is fixed, so the same data always yields the same output.
 */
function titleCandidates(label: string): string[] {
  const room = MAX_TITLE - BRAND.length - 3; // " | "
  return [
    `${label} — Before & After | ${BRAND}`,
    `${label} | ${BRAND}`,
    `${clampWords(label, room)} | ${BRAND}`,
  ];
}

/**
 * Human-readable disambiguator derived from the slug, e.g.
 * "an-anonymous-full-length-transformation-4" -> "Story 4".
 */
function slugSuffix(slug: string) {
  const n = slug.match(/-(\d+)$/)?.[1];
  return n ? `Story ${n}` : slug;
}

/**
 * Build the slug -> title map once, resolving any collision deterministically:
 * stories are processed in their fixed data order, the first claimant keeps the
 * shorter form, and later ones get a slug-derived qualifier appended. The result
 * is a pure function of the story data, so two pages can never share a <title>.
 */
function buildTitleMap(): Map<string, string> {
  const map = new Map<string, string>();
  const taken = new Set<string>();

  for (const { story, slug } of storiesWithSlugs()) {
    const candidates = titleCandidates(story.label);
    let chosen = candidates.find((c) => c.length <= MAX_TITLE && !taken.has(c));

    if (!chosen) {
      // Every preferred form is taken or too long: qualify with the slug.
      const qualifier = slugSuffix(slug);
      const room = MAX_TITLE - BRAND.length - qualifier.length - 6; // " — " + " | "
      const base = clampWords(story.label, Math.max(room, 10));
      chosen = `${base} — ${qualifier} | ${BRAND}`;
      let n = 2;
      while (taken.has(chosen)) chosen = `${base} — ${qualifier} (${n++})`;
    }

    taken.add(chosen);
    map.set(slug, chosen);
  }

  return map;
}

const TITLES = buildTitleMap();

/** Deterministic, globally unique <title> for a story page. */
export function storyTitle(slug: string, story?: Story): string {
  return TITLES.get(slug) ?? `${clampWords(story?.label ?? slug, MAX_TITLE - BRAND.length - 3)} | ${BRAND}`;
}

/** All generated titles, for consistency checks and CI validation. */
export function allStoryTitles(): { slug: string; title: string }[] {
  return [...TITLES].map(([slug, title]) => ({ slug, title }));
}

/**
 * Human-readable headline for a story. Labels that already contain
 * "transformation" (e.g. "An anonymous full-length transformation") would read
 * as "…transformation's weight-loss transformation", so they get a neutral
 * phrase instead of the possessive.
 */
export function storyHeadline(label: string): string {
  // Labels that already read as a phrase (a noun other than a name, an
  // existing possessive, a relative clause or an appositive) would turn into
  // "…back\u2019s weight-loss transformation", so they get a neutral suffix.
  if (isPhraseLabel(label)) return `${label} — weight-loss results`;
  return `${label}\u2019s weight-loss transformation`;
}

/** True when a label is a phrase rather than a plain patient name. */
export function isPhraseLabel(label: string): boolean {
  return (
    /transformation|journey|change|results?\b/i.test(label) ||
    /[\u2019']s\b/i.test(label) ||
    /\bwho\b|\bthat\b/i.test(label) ||
    label.includes(",")
  );
}

/**
 * Alt text for a story share card. Phrase labels skip the possessive so the
 * text never reads "…transformation's".
 */
export function storyCardAlt(label: string, prefix = "Share card"): string {
  return isPhraseLabel(label)
    ? `${prefix}: ${label} — before-and-after weight-loss story`
    : `${prefix} for ${label}'s before-and-after weight-loss story`;
}
